import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation, Link } from 'react-router-dom';
import { Compass } from 'lucide-react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ActivitiesProvider } from './context/ActivitiesContext';
import { ToastProvider, useToast } from './context/ToastContext';
import { RealtimeProvider } from './context/RealtimeContext';
import Layout from './components/Layout';
import CommunitySidebar from './components/CommunitySidebar';
import Home from './pages/Home';
import Live from './pages/Live';
import Explore from './pages/Explore';
import Communities from './pages/Communities';
import Profile from './pages/Profile';
import Room from './pages/Room';
import LiveRoom from './pages/LiveRoom';
import BrainstormRoom from './pages/BrainstormRoom';
import PollRoom from './pages/PollRoom';
import CommunityHub from './pages/CommunityHub';
import CommunityDiscussions from './pages/CommunityDiscussions';
import CommunitySessions from './pages/CommunitySessions';
import CommunityMembers from './pages/CommunityMembers';
import CommunityResources from './pages/CommunityResources';
import ResourceCreation from './pages/ResourceCreation';
import DiscussionRoom from './pages/DiscussionRoom';
import DiscussionCreation from './pages/DiscussionCreation';
import About from './pages/About';
import Contact from './pages/Contact';
import Guidelines from './pages/Guidelines';
import ContentPolicy from './pages/ContentPolicy';
import Disclaimer from './pages/Disclaimer';
import FAQ from './pages/FAQ';
import HelpCenter from './pages/HelpCenter';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

import Onboarding from './pages/Onboarding';
import Login from './pages/Login';
import Signup from './pages/Signup';
import AdminDashboard from './pages/AdminDashboard';
import AdminUsers from './pages/AdminUsers';
import AdminActivities from './pages/AdminActivities';
import AdminCommunities from './pages/AdminCommunities';
import AdminSettings from './pages/AdminSettings';
import AdminAnnouncements from './pages/AdminAnnouncements';
import AdminAnalytics from './pages/AdminAnalytics';
import AdminModeration from './pages/AdminModeration';
import Settings from './pages/Settings';
import RoomCreation from './pages/RoomCreation';
import LiveRoomCreation from './pages/LiveRoomCreation';
import BrainstormCreation from './pages/BrainstormCreation';
import PollCreation from './pages/PollCreation';
import CommunityCreation from './pages/CommunityCreation';
import CommunitySettings from './pages/CommunitySettings';
import Saved from './pages/Saved';
import Notifications from './pages/Notifications';

import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { canManagePlatform } from './utils/permissions';

const QueryProvider = ({ children }) => {
  const { addToast } = useToast();
  
  // Initialize QueryClient inside the provider to have access to useToast
  const [queryClient] = React.useState(() => new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        // Only show toast for non-401 errors (401 is handled by redirecting to login)
        if (error.status !== 401) {
          addToast(error.message || 'Failed to fetch data', 'error');
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        if (error.status !== 401) {
          addToast(error.message || 'Action failed', 'error');
        }
      },
    }),
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  }));

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!canManagePlatform(user)) return <Navigate to="/" replace />;
  return children;
};

const CommunityLayout = ({ children }) => {
  const { id } = useParams();
  return (
    <Layout sidebar={<CommunitySidebar communityId={id} />}>
      {children}
    </Layout>
  );
};

function App() {
  return (
    <AuthProvider>
      <ActivitiesProvider>
        <ToastProvider>
          <QueryProvider>
            <RealtimeProvider>
              <Router>
                <Routes>
            <Route path="/rm/new" element={<Layout hideSidebars><ProtectedRoute><RoomCreation /></ProtectedRoute></Layout>} />
            <Route path="/rm/lv/new" element={<Layout hideSidebars><ProtectedRoute><LiveRoomCreation /></ProtectedRoute></Layout>} />
            <Route path="/rm/bs/new" element={<Layout hideSidebars><ProtectedRoute><BrainstormCreation /></ProtectedRoute></Layout>} />
            <Route path="/rm/pl/new" element={<Layout hideSidebars><ProtectedRoute><PollCreation /></ProtectedRoute></Layout>} />
            <Route path="/rm/lv/:id" element={<Layout hideSidebars><ProtectedRoute><LiveRoom /></ProtectedRoute></Layout>} />
            <Route path="/rm/bs/:id" element={<Layout hideSidebars><ProtectedRoute><BrainstormRoom /></ProtectedRoute></Layout>} />
            <Route path="/rm/pl/:id" element={<Layout hideSidebars><ProtectedRoute><PollRoom /></ProtectedRoute></Layout>} />
            <Route path="/rm/:id" element={<Layout hideSidebars><ProtectedRoute><Room /></ProtectedRoute></Layout>} />
            
            <Route path="/c/new" element={<Layout hideSidebars><ProtectedRoute><CommunityCreation /></ProtectedRoute></Layout>} />
            <Route path="/c/:id" element={<CommunityLayout><ProtectedRoute><CommunityHub /></ProtectedRoute></CommunityLayout>} />
            <Route path="/c/:id/d" element={<CommunityLayout><ProtectedRoute><CommunityDiscussions /></ProtectedRoute></CommunityLayout>} />
            <Route path="/c/:id/s" element={<CommunityLayout><ProtectedRoute><CommunitySessions /></ProtectedRoute></CommunityLayout>} />
            <Route path="/c/:id/m" element={<CommunityLayout><ProtectedRoute><CommunityMembers /></ProtectedRoute></CommunityLayout>} />
            <Route path="/c/:id/r" element={<CommunityLayout><ProtectedRoute><CommunityResources /></ProtectedRoute></CommunityLayout>} />
            <Route path="/c/:id/r/new" element={<Layout hideSidebars><ProtectedRoute><ResourceCreation /></ProtectedRoute></Layout>} />
            <Route path="/c/:id/settings" element={<CommunityLayout><ProtectedRoute><CommunitySettings /></ProtectedRoute></CommunityLayout>} />
            <Route path="/c/:id/d/new" element={<Layout hideSidebars><ProtectedRoute><DiscussionCreation /></ProtectedRoute></Layout>} />
            
            <Route path="/d/:id" element={<CommunityLayout><ProtectedRoute><DiscussionRoom /></ProtectedRoute></CommunityLayout>} />

            <Route path="/onboarding" element={<Layout hideSidebars><Onboarding /></Layout>} />
            <Route path="/login" element={<Layout hideSidebars><Login /></Layout>} />
            <Route path="/signup" element={<Layout hideSidebars><Signup /></Layout>} />

            <Route path="/" element={<Layout><Home /></Layout>} />
            <Route path="/admin" element={<Layout><AdminRoute><AdminDashboard /></AdminRoute></Layout>} />
            <Route path="/admin/users" element={<Layout><AdminRoute><AdminUsers /></AdminRoute></Layout>} />
            <Route path="/admin/activities" element={<Layout><AdminRoute><AdminActivities /></AdminRoute></Layout>} />
            <Route path="/admin/communities" element={<Layout><AdminRoute><AdminCommunities /></AdminRoute></Layout>} />
            <Route path="/admin/announcements" element={<Layout><AdminRoute><AdminAnnouncements /></AdminRoute></Layout>} />
            <Route path="/admin/settings" element={<Layout><AdminRoute><AdminSettings /></AdminRoute></Layout>} />
            <Route path="/admin/analytics" element={<Layout><AdminRoute><AdminAnalytics /></AdminRoute></Layout>} />
            <Route path="/admin/moderation" element={<Layout><AdminRoute><AdminModeration /></AdminRoute></Layout>} />
            <Route path="/communities" element={<Layout><ProtectedRoute><Communities /></ProtectedRoute></Layout>} />
            <Route path="/explore" element={<Layout><ProtectedRoute><Explore /></ProtectedRoute></Layout>} />
            <Route path="/live" element={<Layout><ProtectedRoute><Live /></ProtectedRoute></Layout>} />
            <Route path="/profile" element={<Layout><ProtectedRoute><Profile /></ProtectedRoute></Layout>} />
            <Route path="/settings" element={<Layout><ProtectedRoute><Settings /></ProtectedRoute></Layout>} />
            <Route path="/saved" element={<Layout><ProtectedRoute><Saved /></ProtectedRoute></Layout>} />
            <Route path="/notifications" element={<Layout><ProtectedRoute><Notifications /></ProtectedRoute></Layout>} />
            
            <Route path="/about" element={<Layout><About /></Layout>} />
            <Route path="/contact" element={<Layout><Contact /></Layout>} />
            <Route path="/guidelines" element={<Layout><Guidelines /></Layout>} />
            <Route path="/content-policy" element={<Layout><ContentPolicy /></Layout>} />
            <Route path="/disclaimer" element={<Layout><Disclaimer /></Layout>} />
            <Route path="/faq" element={<Layout><FAQ /></Layout>} />
            <Route path="/help" element={<Layout><HelpCenter /></Layout>} />
            <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />
            <Route path="/terms" element={<Layout><TermsOfService /></Layout>} />

            <Route path="*" element={<Layout hideSidebars><div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
              <div className="w-20 h-20 bg-brand-indigo/10 rounded-3xl flex items-center justify-center text-brand-indigo mb-6">
                <Compass size={40} />
              </div>
              <h1 className="text-4xl font-black text-gray-900 mb-2">404</h1>
              <p className="text-xl font-bold text-gray-600 mb-8">Oops! This page doesn't exist.</p>
              <Link to="/" className="btn-brand px-8 py-4">Back to Home</Link>
            </div></Layout>} />
          </Routes>
        </Router>
            </RealtimeProvider>
        </QueryProvider>
        </ToastProvider>
      </ActivitiesProvider>
    </AuthProvider>
  );
}

export default App;
